import {
  JobConditionAddDialogComponent,
  JobConditions,
} from '@agent-ds/shared/components/job-condition-add-dialog/job-condition-add-dialog.component';
import { ALL_GRAD_CATEGORIES, GradCategory, SalaryTableItemPrefix } from '@agent-ds/shared/interfaces/job-az-check';
import { FieldMeta, RowMeta } from '@agent-ds/shared/models';
import { DialogService, DynamicFieldService } from '@agent-ds/shared/services';
import {
  formatFixedLateNightOvertimeDescription,
  formatFixedOvertimeDescription,
  formatSalaryByGraduateCategoryDescription,
  resolveConditionGradCategories,
} from '../util/job-salary-table';
import { buildSalaryDescriptionNonEditableText, buildSalaryTableKey } from '@agent-ds/shared/util/salary-table-key';
import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

// 給与補足・固定残業代補足が文字数上限を超えている際に、編集不可/編集可能どちらのtextareaにも
// 赤枠を表示するために付与するCSSクラス（dynamic-form.component.scssで定義）。
const DESCRIPTION_OVERFLOW_CLASS = 'job-salary-table__description--overflow';

// salaryTable（dynamicField: job.salaryTable, fieldType: job-salary-table）を表示する画面で共通して必要になる、
// 種別追加ボタン・反映系ボタンのrunnable後付けと、卒業種別ごとの行の表示/非表示切り替えをまとめたヘルパー。
// job-detail-tab.component.ts（求人詳細画面）・job-az-check.service.ts（求人票自動チェック確認画面）の
// 2箇所からそれぞれのコンポーネント単位のprovidersで提供し、独立したインスタンスとして利用する
// （rows・salaryTableは呼び出し元ごとに異なるため状態は共有しない）。
// getSalaryTable・keyPrefixは呼び出し元ごとに異なる実行時の値のためコンストラクタでは受け取れず、init()で設定する。
@Injectable()
export class SalaryTableRowsService {
  rows: RowMeta[] = [];
  private getSalaryTable!: () => { [key: string]: any };
  // getFormRows呼び出し時のbaseKeyによって、fieldのnameが`salaryTable.xxx`または`dynamicData.salaryTable.xxx`のいずれかになるため、
  // 呼び出し元ごとに実際のprefixを指定する。
  private keyPrefix: string = 'salaryTable';

  constructor(
    private dialog: DialogService,
    private dynamicFieldService: DynamicFieldService,
  ) {}

  init(getSalaryTable: () => { [key: string]: any }, keyPrefix: string = 'salaryTable'): void {
    this.getSalaryTable = getSalaryTable;
    this.keyPrefix = keyPrefix;
  }

  findField(fieldName: string): FieldMeta | undefined {
    for (const row of this.rows) {
      const field = row.fields.find((f) => f.name === `${this.keyPrefix}.${fieldName}`);
      if (field) {
        return field;
      }
    }
    return undefined;
  }

  /**
   * salaryTable内の卒業種別1件分（改行用の空フィールド・種別ラベル・入力欄・削除ボタン）の表示/非表示を切り替える。
   */
  setCategoryVisible(valuePrefixes: SalaryTableItemPrefix[], category: GradCategory, breakNamePrefix: string, visible: boolean): void {
    const fieldNames = valuePrefixes.map((prefix) => buildSalaryTableKey(prefix, category));
    const lineBreakName = `lineBreak_${breakNamePrefix}_${category}`;
    const namesToToggle = [lineBreakName, `${fieldNames[0]}.categoryLabel`, ...fieldNames, `${fieldNames[0]}.deleteButton`];
    namesToToggle.forEach((name) => {
      const field = this.findField(name);
      if (field) {
        field.hidden = !visible;
        if (field.actions) {
          // 削除ボタンはactions付きfieldのためhiddenだけでは非表示にならず、style.displayも併せて切り替える。
          field.style = visible ? {} : { display: 'none' };
        } else if (name === lineBreakName) {
          // 改行用fieldはclass:'full'で常に専用のflex行を作るため、hiddenのみだと高さ0の行として
          // gap分の余白だけが残り続ける（非表示の種別が連続すると余白が積み重なって目立つ）。
          // height:0等の基本スタイルは保持したまま、displayだけ切り替えて行自体をレイアウトから除外する。
          field.style = { ...field.style, display: visible ? undefined : 'none' };
        }
      }
    });
  }

  /**
   * salaryTableの卒業種別ごとの項目（月給・固定残業代補足・固定深夜割増手当）について、
   * 値が入っている項目のみを表示するようhidden状態を再計算する。
   */
  syncVisibility(): void {
    if (!this.rows.length) {
      return;
    }
    const salaryTable = this.getSalaryTable() || {};
    ALL_GRAD_CATEGORIES.forEach((category) => {
      this.setCategoryVisible(['salary_'], category, 'salary', salaryTable[buildSalaryTableKey('salary_', category)] != null);
      this.setCategoryVisible(
        ['fixedOvertimeHours_', 'fixedOvertimeAmount_'],
        category,
        'fixedOvertime',
        salaryTable[buildSalaryTableKey('fixedOvertimeHours_', category)] != null ||
          salaryTable[buildSalaryTableKey('fixedOvertimeAmount_', category)] != null,
      );
      this.setCategoryVisible(
        ['fixedLateNightOvertimeHours_', 'fixedLateNightOvertimeAmount_'],
        category,
        'fixedLateNightOvertime',
        salaryTable[buildSalaryTableKey('fixedLateNightOvertimeHours_', category)] != null ||
          salaryTable[buildSalaryTableKey('fixedLateNightOvertimeAmount_', category)] != null,
      );
    });
  }

  /**
   * 固定残業代（salaryTable.fixedOvertimeFee）の入力欄の活性/非活性を切り替える。
   * みなし労働時間制の有無が「有」から始まる場合に固定残業代を編集不可にするために使用する。
   */
  setFixedOvertimeFeeDisabled(disabled: boolean): void {
    const field = this.findField('fixedOvertimeFee');
    if (field) {
      field.disabled = disabled;
    }
  }

  openConditionDialog(
    conditionKey: 'salary' | 'fixedOvertime' | 'fixedLateNightOvertime',
    apply: (conditions: JobConditions) => void,
  ): void {
    const dialogRef = this.dialog.open(JobConditionAddDialogComponent, { data: { conditionKey: conditionKey, salaryTable: this.getSalaryTable() } });
    let closeSubscription: Subscription;
    const unsubscribe = () => {
      if (closeSubscription) {
        closeSubscription.unsubscribe();
      }
    };
    closeSubscription = dialogRef.afterClosed.subscribe((conditions: JobConditions) => {
      unsubscribe();
      if (!conditions) {
        return;
      }
      apply(conditions);
    });
  }

  /**
   * salaryTableの種別追加ボタン・反映系ボタンの実処理を後付けする。
   * DynamicFieldService.getFormRows()はDialogServiceを保持していないため、
   * DialogServiceを保持しているこちら側でrunnableの実体を組み立てる。
   */
  wire(): void {
    const attachAddButton = (
      buttonName: string,
      conditionKey: 'salary' | 'fixedOvertime' | 'fixedLateNightOvertime',
      apply: (setValue: (key: string, value: any) => void, category: GradCategory, conditions: JobConditions) => void,
    ) => {
      const field = this.findField(buttonName);
      if (!field || !field.actions || !field.actions[0]) {
        return;
      }
      field.actions[0].runnable = (getValue: (key: string, override?: boolean) => any, setValue: (key: string, value: any) => void) => {
        this.openConditionDialog(conditionKey, (conditions) => {
          resolveConditionGradCategories(conditions).forEach((category) => apply(setValue, category, conditions));
        });
      };
    };

    attachAddButton('addSalaryCategoryButton', 'salary', (setValue, category, conditions) => {
      if (conditions.amount == null) {
        return;
      }
      setValue(`${this.keyPrefix}.${buildSalaryTableKey('salary_', category)}`, conditions.amount);
      this.setCategoryVisible(['salary_'], category, 'salary', true);
    });

    attachAddButton('addFixedOvertimeCategoryButton', 'fixedOvertime', (setValue, category, conditions) => {
      if (conditions.amount == null || conditions.hours == null) {
        return;
      }
      setValue(`${this.keyPrefix}.${buildSalaryTableKey('fixedOvertimeHours_', category)}`, conditions.hours);
      setValue(`${this.keyPrefix}.${buildSalaryTableKey('fixedOvertimeAmount_', category)}`, conditions.amount);
      this.setCategoryVisible(['fixedOvertimeHours_', 'fixedOvertimeAmount_'], category, 'fixedOvertime', true);
    });

    attachAddButton('addFixedLateNightOvertimeCategoryButton', 'fixedLateNightOvertime', (setValue, category, conditions) => {
      if (conditions.amount == null || conditions.hours == null) {
        return;
      }
      setValue(`${this.keyPrefix}.${buildSalaryTableKey('fixedLateNightOvertimeHours_', category)}`, conditions.hours);
      setValue(`${this.keyPrefix}.${buildSalaryTableKey('fixedLateNightOvertimeAmount_', category)}`, conditions.amount);
      this.setCategoryVisible(['fixedLateNightOvertimeHours_', 'fixedLateNightOvertimeAmount_'], category, 'fixedLateNightOvertime', true);
    });

    const attachReflectButton = (buttonName: string, targetDescriptionKey: string, compute: () => string) => {
      const field = this.findField(buttonName);
      if (!field || !field.actions || !field.actions[0]) {
        return;
      }
      field.actions[0].runnable = (getValue: (key: string, override?: boolean) => any, setValue: (key: string, value: any) => void) => {
        setValue(`${this.keyPrefix}.${targetDescriptionKey}`, compute());
      };
    };

    attachReflectButton('fixedOvertimeDescriptionReflectButton', 'fixedOvertimeDescriptionNonEditable', () =>
      formatFixedOvertimeDescription(this.getSalaryTable() || {}, false),
    );
    attachReflectButton('fixedOvertimeDescriptionShortenButton', 'fixedOvertimeDescriptionNonEditable', () =>
      formatFixedOvertimeDescription(this.getSalaryTable() || {}, true),
    );

    attachReflectButton('fixedLateNightOvertimeDescriptionReflectButton', 'salaryDescriptionFixedLateNightOvertime', () =>
      formatFixedLateNightOvertimeDescription(this.getSalaryTable() || {}, false),
    );
    attachReflectButton('fixedLateNightOvertimeDescriptionShortenButton', 'salaryDescriptionFixedLateNightOvertime', () =>
      formatFixedLateNightOvertimeDescription(this.getSalaryTable() || {}, true),
    );

    attachReflectButton('salaryDescriptionReflectButton', 'salaryDescriptionSalaryByGraduateCategory', () =>
      formatSalaryByGraduateCategoryDescription(this.getSalaryTable() || {}, false),
    );
    attachReflectButton('salaryDescriptionShortenButton', 'salaryDescriptionSalaryByGraduateCategory', () =>
      formatSalaryByGraduateCategoryDescription(this.getSalaryTable() || {}, true),
    );
  }

  /**
   * 給与補足・固定残業代補足について、編集不可欄と編集可能欄を改行で結合した文字数が
   * DynamicField定義の上限（salaryDescription: 67、fixedOvertimeFeeDescription: 53）を超えていないかを判定し、
   * 各欄のmaxバリデーションと上限超過時の赤枠クラスを更新する。
   * 呼び出し元は本メソッドの後にフォームの再構築（DynamicFormComponentのmetadataDiffer検知）をトリガーする必要がある。
   */
  updateDescriptionValidation(): void {
    if (!this.rows.length) {
      return;
    }
    const salaryTable = this.getSalaryTable() || {};

    this.updateDescriptionPairValidation(
      'salaryDescriptionEditable',
      'salaryDescriptionNonEditableDisplay',
      'salaryDescription',
      buildSalaryDescriptionNonEditableText(salaryTable),
      salaryTable.salaryDescriptionEditable,
    );

    this.updateDescriptionPairValidation(
      'fixedOvertimeDescriptionEditable',
      'fixedOvertimeDescriptionNonEditable',
      'fixedOvertimeFeeDescription',
      salaryTable.fixedOvertimeDescriptionNonEditable || '',
      salaryTable.fixedOvertimeDescriptionEditable,
    );
  }

  private updateDescriptionPairValidation(
    editableFieldName: string,
    nonEditableFieldName: string,
    dynamicFieldName: string,
    nonEditableText: string,
    editableText: string,
  ): void {
    const editableField = this.findField(editableFieldName);
    const nonEditableField = this.findField(nonEditableFieldName);
    if (!editableField && !nonEditableField) {
      return;
    }

    const definition = this.dynamicFieldService.getDefinition('job', dynamicFieldName);
    const max = definition && definition.validationStyle && definition.validationStyle.max;
    if (max == null) {
      return;
    }

    const nonEditableLength = (nonEditableText || '').length;
    const editableLength = (editableText || '').length;
    // 保存値（mach-api: syncLegacySalaryFields）と同じく空欄を除いて改行で結合するため、双方入力時は区切りの改行1文字も数える
    const nonEditableLengthWithSeparator = nonEditableLength > 0 ? nonEditableLength + 1 : 0;
    const editableLengthWithSeparator = editableLength > 0 ? editableLength + 1 : 0;
    // 各欄のmaxは「上限 − 相手側の文字数（改行込み）」。編集不可欄だけで上限を超える場合、
    // 空の編集可能欄は自身のmax違反にならないため、赤枠はoverflowクラスで担保する
    if (editableField) {
      editableField.validators = { ...editableField.validators, max: Math.max(max - nonEditableLengthWithSeparator, 0) };
    }
    if (nonEditableField) {
      nonEditableField.validators = { ...nonEditableField.validators, max: Math.max(max - editableLengthWithSeparator, 0) };
    }

    const separatorLength = nonEditableLength > 0 && editableLength > 0 ? 1 : 0;
    const overflow = nonEditableLength + separatorLength + editableLength > max;
    this.setOverflowClass(editableField, overflow);
    this.setOverflowClass(nonEditableField, overflow);
  }

  private setOverflowClass(field: FieldMeta | undefined, overflow: boolean): void {
    if (!field) {
      return;
    }
    const classes = (field.class || '').split(' ').filter((c) => c && c !== DESCRIPTION_OVERFLOW_CLASS);
    if (overflow) {
      classes.push(DESCRIPTION_OVERFLOW_CLASS);
    }
    field.class = classes.join(' ');
  }
}
